import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api, { BASE_URL, fotoUrl } from '../api';
import { useTheme } from '../contexts/ThemeContext';

// ─── Tipler ───────────────────────────────────────────────────────────────────
interface Konusma {
  id: string; tip: 'sinif' | 'direkt' | 'okul'; baslik: string; ders_id: string | null;
  son_mesaj: string | null; son_gonderen: string | null; son_mesaj_tarihi: string | null;
  okunmamis: number; uye_sayisi: number;
}
interface Mesaj {
  id: string; konusma_id: string; gonderen_id: string;
  gonderen_ad: string; gonderen_foto_url: string | null;
  icerik: string; olusturuldu: string;
}
interface Uye { kullanici_id: string; kullanici_rol: string; ad: string; foto_url: string | null; }
interface KonusmaDetay { id: string; tip: string; baslik: string; uyeler: Uye[]; }
interface Kullanici { id: string; ad: string; soyad: string; rol: string; foto_url: string | null; }
interface Ders { id: string; ad: string; }

const ZAMAN = (s: string) => {
  const d = new Date(s);
  const bugun = new Date();
  if (d.toDateString() === bugun.toDateString())
    return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
};
const TAM_ZAMAN = (s: string) =>
  new Date(s).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

function Avatar({ ad, foto_url, boyut = 36 }: { ad: string; foto_url?: string | null; boyut?: number }) {
  const [hatali, setHatali] = React.useState(false);
  const url = fotoUrl(foto_url);
  if (url && !hatali) {
    return <img src={url} alt="" onError={() => setHatali(true)}
      style={{ width: boyut, height: boyut, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  }
  return <div style={{ width: boyut, height: boyut, borderRadius: '50%', background: '#EEEDFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: boyut * 0.36, fontWeight: 700, color: '#534AB7', flexShrink: 0 }}>
    {ad?.charAt(0)?.toUpperCase()}
  </div>;
}

// ─── Ana bileşen ──────────────────────────────────────────────────────────────
export default function MesajlarSayfasi() {
  usePageTitle('Mesajlar');
  const { kullanici } = useAuth();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';
  const [konusmalar, setKonusmalar] = useState<Konusma[]>([]);
  const [seciliId, setSeciliId] = useState<string | null>(null);
  const [konusmaDetay, setKonusmaDetay] = useState<KonusmaDetay | null>(null);
  const [mesajlar, setMesajlar] = useState<Mesaj[]>([]);
  const [yeniMesaj, setYeniMesaj] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [yeniModal, setYeniModal] = useState(false);

  const geriLink = kullanici?.rol === 'egitmen' ? '/egitmen' : kullanici?.rol === 'ogrenci' ? '/profilim' : null;

  const sonMesajRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const konusmalariYukle = useCallback(async () => {
    try {
      const r = await api.get('/api/mesajlar/konusmalar');
      setKonusmalar(r.data);
    } catch { /* sessiz */ }
  }, []);

  const mesajlariYukle = useCallback(async (id: string) => {
    try {
      const r = await api.get(`/api/mesajlar/konusmalar/${id}`);
      setKonusmaDetay(r.data.konusma);
      setMesajlar(r.data.mesajlar);
      setKonusmalar(prev => prev.map(k => k.id === id ? { ...k, okunmamis: 0 } : k));
    } catch { /* sessiz */ }
  }, []);

  useEffect(() => { konusmalariYukle(); }, [konusmalariYukle]);

  useEffect(() => {
    if (!seciliId) return;
    mesajlariYukle(seciliId);
    pollingRef.current = setInterval(() => mesajlariYukle(seciliId), 5000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [seciliId, mesajlariYukle]);

  useEffect(() => {
    sonMesajRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mesajlar]);

  const konusmaAc = (id: string) => {
    setSeciliId(id);
  };

  const gonder = async () => {
    if (!yeniMesaj.trim() || !seciliId || gonderiliyor) return;
    setGonderiliyor(true);
    const icerik = yeniMesaj.trim();
    setYeniMesaj('');
    try {
      const r = await api.post(`/api/mesajlar/konusmalar/${seciliId}/gonder`, { icerik });
      setMesajlar(prev => [...prev, r.data]);
      konusmalariYukle();
    } catch { setYeniMesaj(icerik); }
    finally { setGonderiliyor(false); inputRef.current?.focus(); }
  };

  const totalOkunmamis = konusmalar.reduce((s, k) => s + Number(k.okunmamis), 0);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 56px)', overflow: 'hidden', background: isDark ? '#1c1c1c' : '#f5f5f5' }}>

      {/* ── Sol: Konuşma listesi ── */}
      <div style={{ width: 300, flexShrink: 0, borderRight: `1px solid ${border}`, background: card, display: 'flex', flexDirection: 'column' }}>
        {/* Geri butonu (admin dışı) */}
        {geriLink && (
          <button onClick={() => navigate(geriLink)} style={{ margin: '10px 12px 0', display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: textSec, fontSize: 12, padding: '4px 6px', borderRadius: 6, width: 'fit-content' }}>
            ← {kullanici?.rol === 'egitmen' ? 'Panele dön' : 'Profile dön'}
          </button>
        )}
        {/* Başlık */}
        <div style={{ padding: '10px 16px 12px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>
            Mesajlar
            {totalOkunmamis > 0 && (
              <span style={{ marginLeft: 8, background: '#534AB7', color: 'white', borderRadius: 20, padding: '1px 8px', fontSize: 11 }}>{totalOkunmamis}</span>
            )}
          </div>
          <button onClick={() => setYeniModal(true)} style={{ background: '#534AB7', color: 'white', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            + Yeni
          </button>
        </div>

        {/* Liste */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {konusmalar.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: textMuted, fontSize: 13 }}>Henüz konuşma yok</div>
          ) : konusmalar.map(k => {
            const secili = k.id === seciliId;
            return (
              <div key={k.id} onClick={() => konusmaAc(k.id)} style={{
                padding: '12px 14px', cursor: 'pointer', borderBottom: `1px solid ${isDark ? '#1c1c1c' : '#f5f5f5'}`,
                background: secili ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : card,
                transition: 'background .1s',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'white', background: k.tip === 'sinif' ? '#1D9E75' : k.tip === 'okul' ? '#C41E3A' : '#534AB7' }}>
                    {k.tip === 'sinif' ? '👥' : k.tip === 'okul' ? '🎓' : k.baslik.charAt(0)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: secili || Number(k.okunmamis) > 0 ? 700 : 500, color: textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{k.baslik}</span>
                      <span style={{ fontSize: 10, color: textMuted, flexShrink: 0, marginLeft: 4 }}>{k.son_mesaj_tarihi ? ZAMAN(k.son_mesaj_tarihi) : ''}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                      <span style={{ fontSize: 12, color: textSec, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170 }}>
                        {k.son_mesaj ? `${k.son_gonderen?.split(' ')[0]}: ${k.son_mesaj}` : 'Henüz mesaj yok'}
                      </span>
                      {Number(k.okunmamis) > 0 && (
                        <span style={{ background: '#534AB7', color: 'white', borderRadius: 20, padding: '1px 7px', fontSize: 10, flexShrink: 0 }}>{k.okunmamis}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Sağ: Mesaj alanı ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!seciliId ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: textMuted, fontSize: 14, flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 32 }}>💬</div>
            <div>Bir konuşma seçin</div>
          </div>
        ) : (
          <>
            {/* Chat başlığı */}
            <div style={{ padding: '12px 18px', borderBottom: `1px solid ${border}`, background: card, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, background: konusmaDetay?.tip === 'sinif' ? isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE' : isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', flexShrink: 0 }}>
                {konusmaDetay?.tip === 'sinif' ? '👥' : '💬'}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{konusmaDetay?.baslik}</div>
                <div style={{ fontSize: 11, color: textMuted }}>
                  {konusmaDetay?.uyeler?.length} üye
                  {konusmaDetay?.tip === 'sinif' && ' · Sınıf grubu'}
                </div>
              </div>
            </div>

            {/* Mesajlar */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {mesajlar.length === 0 && (
                <div style={{ textAlign: 'center', color: textMuted, fontSize: 13, marginTop: 40 }}>Henüz mesaj yok. İlk mesajı sen at!</div>
              )}
              {mesajlar.map((m, i) => {
                const benim = m.gonderen_id === kullanici?.id;
                const onceki = i > 0 ? mesajlar[i - 1] : null;
                const yeniGrup = !onceki || onceki.gonderen_id !== m.gonderen_id;
                return (
                  <div key={m.id} style={{ display: 'flex', flexDirection: benim ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 8, marginTop: yeniGrup ? 8 : 2 }}>
                    {!benim && (
                      <div style={{ width: 28, flexShrink: 0 }}>
                        {yeniGrup && <Avatar ad={m.gonderen_ad} foto_url={m.gonderen_foto_url} boyut={28} />}
                      </div>
                    )}
                    <div style={{ maxWidth: '65%', display: 'flex', flexDirection: 'column', alignItems: benim ? 'flex-end' : 'flex-start' }}>
                      {yeniGrup && !benim && (
                        <span style={{ fontSize: 11, color: textMuted, marginBottom: 3, marginLeft: 4 }}>{m.gonderen_ad}</span>
                      )}
                      <div title={TAM_ZAMAN(m.olusturuldu)} style={{
                        background: benim ? '#534AB7' : card,
                        color: benim ? 'white' : textPrimary,
                        borderRadius: benim ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        padding: '9px 13px', fontSize: 13, lineHeight: 1.5,
                        border: benim ? 'none' : `1px solid ${border}`,
                        wordBreak: 'break-word',
                      }}>
                        {m.icerik}
                      </div>
                      {(i === mesajlar.length - 1 || mesajlar[i + 1]?.gonderen_id !== m.gonderen_id) && (
                        <span style={{ fontSize: 10, color: textMuted, marginTop: 2, marginLeft: 4, marginRight: 4 }}>{ZAMAN(m.olusturuldu)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={sonMesajRef} />
            </div>

            {/* Mesaj giriş kutusu */}
            <div style={{ padding: '12px 18px', borderTop: `1px solid ${border}`, background: card, display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <textarea
                ref={inputRef}
                value={yeniMesaj}
                onChange={e => setYeniMesaj(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); gonder(); } }}
                placeholder="Mesajınızı yazın... (Enter ile gönder)"
                rows={1}
                style={{
                  flex: 1, padding: '9px 13px', borderRadius: 20, border: `1px solid ${border}`,
                  fontSize: 13, resize: 'none', outline: 'none', fontFamily: 'sans-serif',
                  maxHeight: 120, overflowY: 'auto', lineHeight: 1.5,
                  background: card, color: textPrimary,
                }}
                onInput={e => {
                  const t = e.currentTarget;
                  t.style.height = 'auto';
                  t.style.height = Math.min(t.scrollHeight, 120) + 'px';
                }}
              />
              <button onClick={gonder} disabled={!yeniMesaj.trim() || gonderiliyor}
                style={{
                  background: '#534AB7', color: 'white', border: 'none', borderRadius: '50%',
                  width: 38, height: 38, cursor: 'pointer', fontSize: 16, flexShrink: 0,
                  opacity: !yeniMesaj.trim() ? .4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                ➤
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Yeni konuşma modalı ── */}
      {yeniModal && (
        <YeniKonusmaModal
          onKapat={() => setYeniModal(false)}
          onOlustur={async (tip, ders_id, uye_idler, baslik) => {
            const r = await api.post('/api/mesajlar/konusmalar', { tip, ders_id, uye_idler, baslik });
            setYeniModal(false);
            await konusmalariYukle();
            konusmaAc(r.data.id);
          }}
        />
      )}
    </div>
  );
}

// ─── Yeni konuşma modalı ──────────────────────────────────────────────────────
function YeniKonusmaModal({
  onKapat,
  onOlustur,
}: {
  onKapat: () => void;
  onOlustur: (tip: string, ders_id: string | null, uye_idler: any[], baslik: string) => Promise<void>;
}) {
  const { kullanici } = useAuth();
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';
  const [sekme, setSekme] = useState<'sinif' | 'direkt'>('sinif');
  const [dersler, setDersler] = useState<Ders[]>([]);
  const [kullanicilar, setKullanicilar] = useState<{ ogrenciler: Kullanici[]; egitmenler: Kullanici[] }>({ ogrenciler: [], egitmenler: [] });
  const [seciliDersId, setSeciliDersId] = useState('');
  const [seciliUyeler, setSeciliUyeler] = useState<Kullanici[]>([]);
  const [arama, setArama] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [olusturuluyor, setOlusturuluyor] = useState(false);

  useEffect(() => {
    setYukleniyor(true);
    Promise.all([
      api.get('/api/mesajlar/dersler'),
      api.get('/api/mesajlar/kullanicilar'),
    ]).then(([dR, kR]) => {
      setDersler(dR.data);
      setKullanicilar(kR.data);
    }).finally(() => setYukleniyor(false));
  }, []);

  const adminler = kullanicilar.egitmenler.filter((k: Kullanici) => (k as any).rol === 'admin');
  const gercekEgitmenler = kullanicilar.egitmenler.filter((k: Kullanici) => (k as any).rol !== 'admin');
  const tumKullanicilar = [...adminler, ...gercekEgitmenler, ...kullanicilar.ogrenciler];
  const filtreliKullanicilar = tumKullanicilar.filter(k =>
    `${k.ad} ${k.soyad}`.toLowerCase().includes(arama.toLowerCase())
  );

  const uyeToggle = (k: Kullanici) => {
    setSeciliUyeler(prev =>
      prev.find(u => u.id === k.id) ? prev.filter(u => u.id !== k.id) : [...prev, k]
    );
  };

  const olustur = async () => {
    setOlusturuluyor(true);
    try {
      if (sekme === 'sinif') {
        if (!seciliDersId) return;
        await onOlustur('sinif', seciliDersId, [], '');
      } else {
        if (!seciliUyeler.length) return;
        const uye_idler = seciliUyeler.map(u => ({ id: u.id, rol: u.rol, ad: `${u.ad} ${u.soyad}` }));
        const baslik = seciliUyeler.map(u => `${u.ad} ${u.soyad}`).join(', ');
        await onOlustur('direkt', null, uye_idler, baslik);
      }
    } finally { setOlusturuluyor(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onKapat(); }}>
      <div style={{ background: card, borderRadius: 14, width: '100%', maxWidth: 440, overflow: 'hidden' }}>
        <div style={{ padding: '18px 20px 0', borderBottom: `1px solid ${border}` }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Yeni konuşma</div>
          <div style={{ display: 'flex', gap: 0 }}>
            {([['sinif', '👥 Sınıf grubu'], ['direkt', '💬 Bireysel']] as const).map(([k, l]) => (
              <button key={k} onClick={() => setSekme(k)} style={{
                flex: 1, padding: '8px 0', border: 'none', borderBottom: sekme === k ? '2px solid #534AB7' : '2px solid transparent',
                background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: sekme === k ? 600 : 400,
                color: sekme === k ? '#534AB7' : textSec,
              }}>{l}</button>
            ))}
          </div>
        </div>

        <div style={{ padding: 20, maxHeight: 380, overflowY: 'auto' }}>
          {yukleniyor ? (
            <div style={{ textAlign: 'center', color: textMuted, padding: 32 }}>Yükleniyor...</div>
          ) : sekme === 'sinif' ? (
            <div>
              <div style={{ fontSize: 12, color: textSec, marginBottom: 10 }}>Hangi ders için sınıf grubu oluşturulsun?</div>
              {dersler.length === 0
                ? <div style={{ color: textMuted, fontSize: 13 }}>Erişebileceğiniz ders bulunamadı.</div>
                : dersler.map(d => (
                  <div key={d.id} onClick={() => setSeciliDersId(d.id)} style={{
                    padding: '10px 14px', borderRadius: 8, border: '1px solid',
                    borderColor: seciliDersId === d.id ? '#534AB7' : border,
                    background: seciliDersId === d.id ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : card,
                    marginBottom: 6, cursor: 'pointer', fontSize: 13, fontWeight: seciliDersId === d.id ? 600 : 400,
                    color: seciliDersId === d.id ? '#534AB7' : isDark ? '#e0e0e0' : '#333',
                  }}>
                    {d.ad}
                  </div>
                ))
              }
            </div>
          ) : (
            <div>
              <input value={arama} onChange={e => setArama(e.target.value)}
                placeholder="İsim ara..."
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, fontSize: 13, boxSizing: 'border-box', marginBottom: 10, outline: 'none', background: card, color: isDark ? '#e8e8e8' : undefined }} />
              {seciliUyeler.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                  {seciliUyeler.map(u => (
                    <span key={u.id} style={{ background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', color: '#534AB7', borderRadius: 20, padding: '3px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                      {u.ad} {u.soyad}
                      <span onClick={() => uyeToggle(u)} style={{ cursor: 'pointer', fontWeight: 700 }}>×</span>
                    </span>
                  ))}
                </div>
              )}
              {filtreliKullanicilar.length === 0
                ? <div style={{ color: textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>Kullanıcı bulunamadı.</div>
                : filtreliKullanicilar.map((k, i) => {
                  const secili = !!seciliUyeler.find(u => u.id === k.id);
                  const kRol = (k as any).rol ?? 'ogrenci';
                  const onceki = i > 0 ? (filtreliKullanicilar[i-1] as any).rol : null;
                  const baslikGoster = kRol !== onceki;
                  const basliklar: Record<string, string> = { admin: 'Yönetim', egitmen: 'Eğitmenler', ogrenci: 'Öğrenciler' };
                  const [rolBg, rolRenk]: [string,string] = kRol === 'admin' ? [isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB','#A32D2D'] : kRol === 'egitmen' ? [isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE','#534AB7'] : [isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE','#3B6D11'];
                  return (
                    <React.Fragment key={k.id}>
                      {baslikGoster && (
                        <div style={{ padding: '6px 10px 3px', fontSize: 10, fontWeight: 700, color: rolRenk, background: rolBg, textTransform: 'uppercase', letterSpacing: .5 }}>
                          {basliklar[kRol]}
                        </div>
                      )}
                    <div onClick={() => uyeToggle(k)} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8,
                      cursor: 'pointer', background: secili ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : card, marginBottom: 2,
                    }}>
                      <Avatar ad={k.ad} foto_url={k.foto_url} boyut={32} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{k.ad} {k.soyad}</div>
                        <div style={{ fontSize: 11, color: textMuted }}>{k.rol === 'egitmen' ? 'Eğitmen' : 'Öğrenci'}</div>
                      </div>
                      {secili && <span style={{ color: '#534AB7', fontSize: 16 }}>✓</span>}
                    </div>
                    </React.Fragment>
                  );
                })
              }
            </div>
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: `1px solid ${border}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onKapat} style={{ padding: '8px 16px', borderRadius: 7, border: `1px solid ${isDark ? '#333' : '#ddd'}`, background: card, cursor: 'pointer', fontSize: 13, color: isDark ? '#e8e8e8' : undefined }}>İptal</button>
          <button onClick={olustur} disabled={olusturuluyor || (sekme === 'sinif' ? !seciliDersId : !seciliUyeler.length)}
            style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: '#534AB7', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: (sekme === 'sinif' ? !seciliDersId : !seciliUyeler.length) ? .5 : 1 }}>
            {olusturuluyor ? 'Açılıyor...' : 'Konuşmayı başlat'}
          </button>
        </div>
      </div>
    </div>
  );
}
